'use client';

import { useEffect, useState } from 'react';
import {
  createDefaultHomepageConfig,
  fetchHomepageConfig,
  type HomepageGuideAlbum,
} from '../lib/homepage-config';
import {
  fetchGuidePost,
  fetchGuidePostSummaries,
  type GuidePostData,
} from '../lib/guide-article';
import GuideArticleView from './guide-article-view';

/**
 * 真实用户：页面模块先渲染，正文/专辑由浏览器拉 Go `/api` 填充。
 * 请求经 shareAsync 合并，避免 StrictMode / remount 双打。
 */
export default function GuideArticleClient({
  gameKey,
  gameDomain,
  postId,
  variant = 'default',
}: {
  gameKey: string;
  gameDomain: string;
  postId: string;
  variant?: 'default' | 'v2';
}) {
  const [article, setArticle] = useState<GuidePostData | null>(null);
  const [guideAlbums, setGuideAlbums] = useState<HomepageGuideAlbum[]>([]);
  const [relatedPostTitles, setRelatedPostTitles] = useState<Record<string, string>>(
    {},
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [nextArticle, homepage, summaries] = await Promise.all([
          fetchGuidePost(gameKey, postId),
          fetchHomepageConfig(gameKey).catch(() => createDefaultHomepageConfig(gameKey)),
          fetchGuidePostSummaries(gameKey),
        ]);

        const nextAlbums = homepage.guideAlbums.filter((album) =>
          album.postIds.includes(postId),
        );

        const titles: Record<string, string> = { [postId]: nextArticle.title };
        for (const item of summaries) {
          titles[item.id] = item.title || '未命名攻略';
        }
        for (const album of nextAlbums) {
          for (const id of album.postIds) {
            if (!titles[id]) titles[id] = '未命名攻略';
          }
        }

        if (!cancelled) {
          document.title = `${nextArticle.title} · GAME WIKI`;
          setArticle(nextArticle);
          setGuideAlbums(nextAlbums);
          setRelatedPostTitles(titles);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : '攻略加载失败');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [gameKey, postId]);

  return (
    <GuideArticleView
      gameKey={gameKey}
      gameDomain={gameDomain}
      postId={postId}
      article={article}
      guideAlbums={guideAlbums}
      relatedPostTitles={relatedPostTitles}
      loading={loading}
      error={error}
      variant={variant}
    />
  );
}
